import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, UploadSimple, EnvelopeSimple, MagnifyingGlass, DownloadSimple } from "@phosphor-icons/react";
import api, { formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const EXPORT_SCOPES = [
  { key: "all", label: "Everyone (allow-list ∪ registered)", hint: "Recommended for uploading to a partner platform — maximum coverage." },
  { key: "registered", label: "Registered only", hint: "Delegates who actually signed up and used the app." },
  { key: "allowlist", label: "Allow-list only", hint: "Everyone you cleared for access, signed up or not." },
];

export default function AccessAdmin() {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [bulk, setBulk] = useState("");
  const [busy, setBusy] = useState(false);
  const [exportScope, setExportScope] = useState("all");
  const [exporting, setExporting] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/allowed-attendees");
      setRows(data);
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addBulk = async () => {
    if (!bulk.trim()) { toast.error("Paste at least one email address."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/allowed-attendees", { emails: bulk });
      const parts = [];
      if (data.added) parts.push(`${data.added} added`);
      if (data.skipped_existing) parts.push(`${data.skipped_existing} already on list`);
      toast.success(parts.join(" · ") || "Nothing to add");
      setBulk("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const uploadCsv = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/allowed-attendees/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.added} added · ${data.skipped_existing} already on list (from ${data.filename})`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const remove = async (email) => {
    if (!window.confirm(`Remove ${email} from the attendee allow-list? They won't be able to register a new account (existing accounts stay logged in).`)) return;
    try {
      await api.delete(`/admin/allowed-attendees/${encodeURIComponent(email)}`);
      toast.success(`${email} removed`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const exportEmails = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/admin/emails.csv?scope=${encodeURIComponent(exportScope)}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dftb26_emails_${exportScope}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Email list downloaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setExporting(false); }
  };

  const filtered = rows.filter((r) => !query.trim() || r.email.toLowerCase().includes(query.trim().toLowerCase()));
  const registeredCount = rows.filter((r) => r.registered).length;

  return (
    <div data-testid="access-admin" className="space-y-5">
      <div className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm">
        <div className="flex items-center gap-2 mb-2">
          <EnvelopeSimple size={16} weight="duotone" className="text-[#000000]" />
          <h3 className="font-display text-base">Attendee allow-list</h3>
        </div>
        <p className="text-xs text-[#5a5a5a]">
          Only emails on this list can register for a delegate account. Existing accounts keep working even if you later remove their email - to fully revoke someone, also remove them from the Attendees / Admins tab.
        </p>
        <div className="mt-3 text-xs font-mono-time text-[#7A828C] flex flex-wrap gap-x-4 gap-y-1">
          <span><strong className="text-[#1c1c24]">{rows.length}</strong> on list</span>
          <span><strong className="text-[#000000]">{registeredCount}</strong> registered</span>
          <span><strong className="text-[#7A828C]">{rows.length - registeredCount}</strong> not yet signed up</span>
        </div>
      </div>

      <div className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm" data-testid="access-export-panel">
        <div className="flex items-center gap-2 mb-1">
          <DownloadSimple size={16} weight="duotone" className="text-[#000000]" />
          <h4 className="font-display text-sm">Export email list</h4>
        </div>
        <p className="text-xs text-[#5a5a5a] mb-3 leading-relaxed">
          Emails-only CSV - one address per line. Useful for uploading to a partner platform
          (e.g. the allow-list on <span className="font-mono-time">virtual.dftb26.com</span>).
        </p>
        <div className="space-y-1 mb-3">
          {EXPORT_SCOPES.map((s) => (
            <label
              key={s.key}
              className={`flex items-start gap-2 p-2 border rounded-sm cursor-pointer ${exportScope === s.key ? "border-[#000000] bg-[#F5F7FA]" : "border-[#E5E9F0]"}`}
              data-testid={`access-export-scope-${s.key}`}
            >
              <input
                type="radio"
                name="access-export-scope"
                value={s.key}
                checked={exportScope === s.key}
                onChange={() => setExportScope(s.key)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-[#1c1c24]">{s.label}</div>
                <div className="text-[11px] text-[#7A828C] leading-relaxed">{s.hint}</div>
              </div>
            </label>
          ))}
        </div>
        <Button
          onClick={exportEmails}
          disabled={exporting}
          className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]"
          data-testid="access-export-btn"
        >
          <DownloadSimple size={14} className="mr-1" />
          {exporting ? "Preparing…" : "Download CSV"}
        </Button>
      </div>

      <div className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm">
        <h4 className="font-display text-sm mb-2">Add emails</h4>
        <p className="text-xs text-[#7A828C] mb-2">Paste one per line, or comma/space separated. Duplicates are ignored.</p>
        <Textarea
          value={bulk}
          onChange={(e) => setBulk(e.target.value)}
          rows={5}
          placeholder={"alice@hospital.nhs.uk\nbob@example.org\n…"}
          className="rounded-sm font-mono-time text-sm"
          data-testid="access-bulk-input"
        />
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mt-3">
          <Button onClick={addBulk} disabled={busy} className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]" data-testid="access-bulk-add">
            <Plus size={14} className="mr-1" /> Add to list
          </Button>
          <div className="flex-1" />
          <label className="inline-flex items-center gap-2 text-sm text-[#000000] cursor-pointer hover:underline" data-testid="access-csv-trigger">
            <UploadSimple size={14} /> …or upload a CSV / TXT
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt"
              className="hidden"
              onChange={(e) => uploadCsv(e.target.files?.[0])}
              data-testid="access-csv-input"
            />
          </label>
        </div>
      </div>

      <div className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm">
        <div className="flex items-center justify-between mb-3 gap-3">
          <h4 className="font-display text-sm">Allow-list</h4>
          <div className="relative flex-1 max-w-xs">
            <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#7A828C]" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="h-9 rounded-sm pl-7"
              data-testid="access-search"
            />
          </div>
        </div>
        {filtered.length === 0 ? (
          <p className="text-xs text-[#7A828C] py-3">No matching emails on the list.</p>
        ) : (
          <div className="divide-y divide-[#E5E9F0]">
            {filtered.map((r) => (
              <div key={r.email} className="flex items-center justify-between py-2 gap-3" data-testid={`access-row-${r.email}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-mono-time text-sm truncate">{r.email}</div>
                  <div className="text-xs text-[#7A828C]">
                    {r.registered ? (
                      <span className="text-[#000000]">✓ Registered</span>
                    ) : (
                      <span>Awaiting sign-up</span>
                    )}
                    {r.added_by && r.added_by !== "system" && <> · added by {r.added_by}</>}
                  </div>
                </div>
                <button
                  onClick={() => remove(r.email)}
                  aria-label={`Remove ${r.email}`}
                  className="p-2 text-[#DC4F48] hover:opacity-80"
                  data-testid={`access-remove-${r.email}`}
                >
                  <Trash size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
